import {
  resolveAttachmentReferences,
  searchAndShowProductImages,
} from '@frontend/store/creative-studio/chat-function-handlers/search-products';
import {
  normalizeBrowserUrl,
  resolveProductOverlayUrl,
} from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';

/**
 * Stable identity for an image URL (basename, or the `share?file=` filename).
 * Used to detect when the "background" the model picked is actually an image we
 * generated earlier rather than one the user provided this turn.
 */
function imageFileKey(url) {
  if (!url || typeof url !== 'string') return '';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    const parsed = new URL(url, origin);
    const fileParam = parsed.searchParams.get('file');
    const path = fileParam || parsed.pathname;
    return (path.split('/').pop() || '').trim();
  } catch (e) {
    return (url.split('?')[0].split('/').pop() || '').trim();
  }
}

/**
 * Build the [Regenerate] [Add to layout] widget set shown under a placed-product
 * result. Kept here so the handler and the regenerate action stay in sync.
 */
export function buildPlacedProductWidgets() {
  return [
    {
      type: 'action_buttons',
      id: 'placed_product_actions',
      buttons: [
        {
          label: 'Regenerate',
          action: 'regenerate_placed_product',
          variant: 'secondary',
          iconClass: 'bi bi-arrow-repeat',
        },
        {
          label: 'Add to layout',
          action: 'create_layout_from_design_concept',
          variant: 'primary',
          iconClass: 'bi bi-arrow-right',
        },
        {
          label: 'Add as editable layout',
          action: 'add_placed_product_as_layered',
          variant: 'primary',
          iconClass: 'bi bi-layers',
        },
      ],
    },
  ];
}

export async function handlePlaceProductOnBackground($message, { state, commit, dispatch } = {}) {
  let args = {};

  try {
    args = typeof $message.function_call.arguments === 'string'
      ? JSON.parse($message.function_call.arguments)
      : $message.function_call.arguments || {};
  } catch (e) {
    return;
  }

  const savedAttachments = $message.savedAttachments || [];
  const messages = state?.messages || [];

  // The OpenAI context auto-injects the LAST generated image as an extra
  // attachment, appended AFTER the user's own attachments for this turn. The
  // model often mis-picks it as the "background" (a prior "kitchen counter"
  // composite), which stacks the new product onto the previous one. Any
  // savedAttachments index at or beyond the user's real attachment count is
  // that injected image — never a user-provided background or product.
  const lastUserMsg = [...messages].reverse().find((m) => m && m.role === 'user');
  const userAttachmentCount = Array.isArray(lastUserMsg?.attachments)
    ? lastUserMsg.attachments.length
    : 0;
  const isInjectedAttachmentIndex = (idx) => Number.isInteger(idx) && idx >= userAttachmentCount;

  // Build a lookup of images we generated in earlier turns, used as a backstop
  // to reject a previously generated image picked as a URL-type background.
  const priorAssistantImageKeys = new Set();
  for (const m of messages) {
    if (m && m.role === 'assistant' && Array.isArray(m.attachments)) {
      for (const a of m.attachments) {
        if (a && a.payload) priorAssistantImageKeys.add(imageFileKey(a.payload));
      }
    }
  }

  // Resolve the background first so we can exclude its attachment from product resolution.
  let sceneUrl = null;
  let sceneDescription = null;
  const excludeAttachmentIndices = [];
  const bg = args.background_reference;

  if (bg && bg.value !== undefined && bg.value !== null && bg.value !== '') {
    if (bg.type === 'attachment') {
      const idx = parseInt(bg.value, 10);
      // Only honor it as a background when it's one of the user's OWN attachments
      // this turn — not the auto-injected last-generated image.
      if (!Number.isNaN(idx) && !isInjectedAttachmentIndex(idx)) {
        excludeAttachmentIndices.push(idx);
        const [resolvedBg] = resolveAttachmentReferences(
          [{ type: 'attachment', value: String(idx) }],
          savedAttachments,
          messages,
          true,
        );
        if (resolvedBg && resolvedBg.type === 'url' && resolvedBg.value) {
          sceneUrl = normalizeBrowserUrl(resolvedBg.value);
        }
      }
    } else if (bg.type === 'url') {
      sceneUrl = normalizeBrowserUrl(String(bg.value));
    }
  }

  // Backstop: never reuse a previously generated image as the background.
  if (sceneUrl) {
    const sceneKey = imageFileKey(sceneUrl);
    if (sceneKey && priorAssistantImageKeys.has(sceneKey)) {
      sceneUrl = null;
    }
  }

  // Keep the auto-injected last-generated image out of product resolution too,
  // so it can't be grabbed as the product overlay either.
  for (let i = userAttachmentCount; i < savedAttachments.length; i += 1) {
    if (!excludeAttachmentIndices.includes(i)) {
      excludeAttachmentIndices.push(i);
    }
  }

  if (!sceneUrl && args.background_scene) {
    sceneDescription = String(args.background_scene).trim();
  }

  // The scene shown to the user is their own phrasing (e.g. "a kitchen counter"),
  // while the enriched `background_scene` is still what we send to generation.
  const userScenePhrase = args.user_scene_phrase
    ? String(args.user_scene_phrase).trim()
    : '';

  // Resolve the product image (attachments first, then ASIN/GTIN/name via search).
  const productRefs = resolveAttachmentReferences(
    args.product_references || [],
    savedAttachments,
    messages,
    false,
    excludeAttachmentIndices,
  );

  // Only fetch + show a product card for identifiers we have to look up
  // (ASIN/GTIN/name/Salsify). For an image the user already attached/pasted,
  // showing a "Using attached image / Select a different image" card is
  // redundant — they can see it in their own message — so skip it.
  const searchableRefs = productRefs.filter(
    (r) => r.type === 'asin' || r.type === 'gtin' || r.type === 'name' || r.type === 'salsify',
  );

  if (searchableRefs.length) {
    await searchAndShowProductImages(searchableRefs, { commit, dispatch });
  }

  const productUrl = await resolveProductOverlayUrl(productRefs, messages);

  if (!productUrl) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'I need a product to place. Please attach, paste, or select a product image, or give me an ASIN/GTIN/product name.',
    });
    return;
  }

  if (!sceneUrl && !sceneDescription) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Where should I place the product? Describe a background scene, or attach/paste/select a background image.',
    });
    return;
  }

  await dispatch('addAndSaveMessage', {
    role: 'assistant',
    content: sceneUrl
      ? 'Placing your product on the background…'
      : `Placing your product on <b>${userScenePhrase || sceneDescription}</b>…`,
  });

  commit('SET_PROCESSING_CHAT_MESSAGE', true);

  // Case 1 (text scene): generate a clean background first so the realistic
  // composite AND a later editable layout share the exact same scene. Falls
  // back to single-shot if background generation fails.
  let sceneGenerated = false;
  if (!sceneUrl && sceneDescription) {
    try {
      const { data: bgData } = await axios.post('/networks/generate-background-previews', {
        background_scene: sceneDescription,
      });
      const bgUrl = (bgData?.options || [])[0] || null;
      if (bgUrl) {
        sceneUrl = bgUrl.startsWith('http') ? bgUrl : window.location.origin + bgUrl;
        sceneGenerated = true;
      }
    } catch (e) {
      // Fall back to single-shot generation below.
    }
  }

  const payload = {
    product_url: productUrl,
    user_instructions: args.image_prompt || '',
  };
  if (sceneUrl) {
    payload.scene_url = sceneUrl;
  } else {
    payload.scene_description = sceneDescription;
  }

  try {
    const { data } = await axios.post('/networks/compose-scene', payload);
    const compositeUrl = data?.composite_url;

    if (!compositeUrl) {
      throw new Error('No composite URL returned');
    }

    const fullUrl = compositeUrl.startsWith('http')
      ? compositeUrl
      : window.location.origin + compositeUrl;

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Here is your product placed on the background:',
      attachments: [{ type: 'url', payload: fullUrl, name: 'Placed product' }],
      _placeProductContext: {
        product_url: productUrl,
        scene_url: sceneUrl || null,
        scene_description: sceneDescription || null,
        scene_generated: sceneGenerated,
        user_instructions: args.image_prompt || '',
      },
      widgets: buildPlacedProductWidgets(),
    });

    return { fullUrls: [fullUrl] };
  } catch (e) {
    console.warn('Place product on background failed:', e);
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: "Couldn't place the product on the background. Please try again.",
    });
  } finally {
    commit('SET_PROCESSING_CHAT_MESSAGE', false);
  }
}
